import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaBars, FaShoppingCart, FaUser } from "react-icons/fa";
import CategorySlidePanel from "../pages/CategorySlidePanel/CategorySlidePanel";
import CartPopup from "../components/Cart/CartPopup";
import { supabase } from "../lib/supabase";

/**
 * TopNavbar
 * Fixed top navigation with category slide panel, cart hover popup, and user dropdown (Supabase auth).
 */
const TopNavbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [showCartPopup, setShowCartPopup] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handlePopup = () => {
      setShowCartPopup(true);
      cancelHide();
      scheduleHide();
    };
    window.addEventListener("show-cart-popup", handlePopup);
    return () => window.removeEventListener("show-cart-popup", handlePopup);
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      const { data, error } = await supabase.auth.getUser();
      if (!error && data?.user?.email) setUserEmail(data.user.email);
    };
    fetchUser();
  }, []);

  const cancelHide = () => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  };

  const scheduleHide = () => {
    hideTimer.current = setTimeout(() => setShowCartPopup(false), 2000);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUserEmail(null);
    navigate("/");
  };

  return (
    <>
      <div
        className={`fixed top-0 left-0 w-full z-30 px-6 py-[50px] flex justify-between items-center transition-all duration-300 ${
          scrolled
            ? "bg-white/30 backdrop-blur-sm shadow-sm"
            : "bg-gradient-to-b from-white/30 to-transparent backdrop-blur-md"
        }`}
      >
        <div className="flex items-center gap-10 text-lg text-[#5c2f2f] font-semibold">
          <div
            onMouseEnter={() => setIsPanelOpen(true)}
            className="cursor-pointer"
          >
            <FaBars className="text-2xl text-[#5c2f2f] hover:text-[#a25d5d] transition-colors" />
          </div>

          {/* <Link
            to="/"
            className="text-lg font-semibold text-[#5c2f2f] hover:text-[#a25d5d] hover:drop-shadow transition duration-300"
          >
            Home
          </Link> */}
        </div>

        <div className="absolute left-1/2 transform -translate-x-1/2 cursor-pointer">
          <Link to="/">
            <img
              src="/10Elogo.png"
              alt="Logo"
              className="h-60 object-contain"
            />
          </Link>
        </div>

        <div className="flex items-center gap-6 text-lg text-[#5c2f2f] font-semibold">
          <div
            onMouseEnter={() => {
              cancelHide();
              setShowCartPopup(true);
            }}
            onMouseLeave={scheduleHide}
            className="relative cursor-pointer"
            onClick={() => navigate("/checkout/shipping")}
          >
            <FaShoppingCart className="text-xl hover:text-[#a25d5d] hover:drop-shadow transition-all" />

            {showCartPopup && (
              <div
                onMouseEnter={cancelHide}
                onMouseLeave={scheduleHide}
                onClick={(e) => e.stopPropagation()}
              >
                <CartPopup />
              </div>
            )}
          </div>

          <div className="relative group">
            <FaUser className="text-xl cursor-pointer hover:text-[#a25d5d] transition-all" />

            {userEmail ? (
              <div className="absolute right-0 mt-2 w-40 bg-white border border-purple-100 rounded shadow-md opacity-0 group-hover:opacity-100 transition-opacity z-40">
                <div className="px-4 py-2 text-sm text-gray-700 border-b border-purple-100 truncate">
                  {userEmail}
                </div>

                <Link
                  to="/admin/orderManagement"
                  className="block px-4 py-2 text-sm hover:bg-purple-50 text-[#5c2f2f]"
                >
                  See Profile
                </Link>

                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-2 text-sm text-[#c56b6b] hover:bg-[#fbeaea]"
                >
                  Logout
                </button>
              </div>
            ) : (
              <Link
                to="/login-page"
                className="absolute top-0 left-0 w-full h-full z-10"
              />
            )}
          </div>
        </div>
      </div>

      <CategorySlidePanel
        isOpen={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
        scrolled={scrolled}
      />
    </>
  );
};

export default TopNavbar;
